/**
 * Live WebSocket connection indicator with runtime identity.
 *
 * When a runtime is selected (`target != null`), the top-bar shows the
 * tenant slug + userId (instead of a bare ULID) and exposes two
 * affordances on the same line:
 *   • VIEW RUNTIME — links to the "Runtime" sidebar entry ("/", where
 *     the runtime monitoring tabs render). Hidden on "/" itself
 *     (would be a self-link).
 *   • DISCONNECT — clears the active runtime via `clearRuntime()` so
 *     the gateway reconnect loop tears down (target=null → wsUrl
 *     undefined → client.stop()). Always visible when a target is set.
 *
 * The dot color reflects the live gateway state (connected/connecting/
 * disconnected/error); the label stays a stable identity row so the
 * operator doesn't lose context to a transient "Connecting…" string.
 */

import { Monitor, Server, Unplug } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import type { GatewayConnectionState } from "@/types/metrics";
import { cn } from "@/lib/utils";
import { RUNTIME_FLEET_PATH, RUNTIME_PAGE_PATH } from "@/lib/routes";

const stateConfig: Record<GatewayConnectionState, { dotColor: string }> = {
  connected: { dotColor: "bg-healthy cd-live" },
  connecting: { dotColor: "bg-warning animate-pulse" },
  disconnected: { dotColor: "bg-faint" },
  error: { dotColor: "bg-critical" },
};

type ConnectionStatusProps = {
  className?: string;
};

const PILL_CLS =
  "inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs font-medium text-muted transition-colors hover:bg-accent hover:text-foreground";

export function ConnectionStatus({ className }: ConnectionStatusProps) {
  const { state } = useGatewayRpc();
  const { target, isLocalDev, clearRuntime } = useRuntimeContext();
  const { pathname } = useLocation();
  const onRuntimePage = pathname === RUNTIME_PAGE_PATH;
  const navigate = useNavigate();

  // On "/" (RuntimeOverview) the page is built to render a specific
  // runtime's metrics — without a target, useGatewayRpc falls through
  // to the default dev gateway (post-convergence: rejects auth → state
  // stuck on "connecting" → perpetual "Connecting to runtime…"). After
  // an explicit DISCONNECT the operator doesn't want that misleading
  // state; redirect them to the fleet (natural inverse of VIEW RUNTIME).
  // From other pages (REST-only) just clear — they weren't depending
  // on the runtime context.
  const onDisconnect = () => {
    clearRuntime();
    if (onRuntimePage) navigate(RUNTIME_FLEET_PATH);
  };

  // The gateway WS is a PER-RUNTIME connection only consumed by the
  // Runtime monitoring view ("/" → RuntimeOverview + its tabs). On
  // platform-admin pages (REST-only — Orgs/Tasks/Cases/Prompts/…) it is
  // irrelevant, so a "Disconnected" gateway there read as "the whole
  // app is broken". `isLocalDev` is NOT a signal of runtime-context (it
  // is true whenever no runtime is selected). The real discriminator is
  // route: show live gateway state only on the Runtime view or when a
  // runtime is explicitly selected; elsewhere show a calm, accurate
  // "no runtime selected".
  const runtimeInContext = onRuntimePage || target != null;

  let dotColor: string;
  let label: React.ReactNode;

  if (target != null) {
    // A runtime is selected → identity label + live dot.
    dotColor = stateConfig[state].dotColor;
    label = (
      <span className="flex items-baseline gap-1.5">
        <strong className="font-semibold text-foreground">
          {target.orgSlug}
        </strong>
        <span className="font-mono text-[10px] text-faint">{target.userId}</span>
      </span>
    );
  } else if (!runtimeInContext) {
    // Platform page with no selection → calm message + "Runtimes" link.
    dotColor = "bg-faint";
    label = "No runtime selected";
  } else if (state === "connected") {
    dotColor = stateConfig.connected.dotColor;
    label = isLocalDev ? "dev-user" : "(no runtime)";
  } else if (state === "connecting") {
    dotColor = stateConfig.connecting.dotColor;
    label = "Connecting…";
  } else if (state === "error") {
    dotColor = stateConfig.error.dotColor;
    label = "Connection error";
  } else {
    dotColor = stateConfig.disconnected.dotColor;
    label = "Runtime disconnected";
  }

  return (
    <div
      className={cn("flex items-center gap-2 text-sm", className)}
      data-testid="connection-status"
    >
      <span className={cn("h-2.5 w-2.5 rounded-full", dotColor)} />
      <span className="text-muted-foreground">{label}</span>

      {target != null && !onRuntimePage && (
        <Link to={RUNTIME_PAGE_PATH} data-testid="view-runtime" className={PILL_CLS}>
          <Monitor className="h-3 w-3 shrink-0" />
          View Runtime
        </Link>
      )}
      {target != null && (
        <button
          type="button"
          onClick={onDisconnect}
          data-testid="disconnect-runtime"
          title="Disconnect — stops the gateway reconnect loop and returns to no-runtime state"
          className={PILL_CLS}
        >
          <Unplug className="h-3 w-3 shrink-0" />
          Disconnect
        </button>
      )}

      {target == null && (
        // Whenever no runtime is selected — on platform pages ("No runtime
        // selected") AND on the Runtime view in any non-connected state
        // ("Runtime disconnected" / "Connecting…" / "Connection error") —
        // offer a one-click jump to the fleet, which IS the selection hub
        // (its per-row "Runtime" button connects the chosen one). Without
        // this, the disconnected Runtime view had no actionable affordance.
        // testid kept stable for existing e2e.
        <Link to="/runtimes" data-testid="select-runtime" className={PILL_CLS}>
          <Server className="h-3 w-3 shrink-0" />
          Connect Runtime
        </Link>
      )}
    </div>
  );
}
